// Pure completion-tracking logic for the 5-tab editor (US4). Extracted from the
// React components so it is unit-testable in isolation. Mirrors the approved
// click-dummy's per-tab fraction model: 0 = not started (grey), 0<x<1 = partial
// (amber), 1 = done (green).

import type { TierResponse } from '../api/types';

export type TabId = 'details' | 'tools' | 'value' | 'bizcase' | 'improvements';
export type TabState = 'todo' | 'partial' | 'done';

export interface EditorForm {
  name: string;
  description: string;
  /** Selected domain id, or null for "No domain". Methodology-free metadata. */
  domainId: string | null;
  /** 'strategic' | 'monetary' | null — the selected primary value type. */
  valueType: 'strategic' | 'monetary' | null;
  /** Strategic importance — one of {1, 3, 5, 7, 9}, 0 = not set. */
  importance: number;
  /** Optional free-text justification for the strategic value rating. */
  justification: string;
  /** Estimated annual monetary value (€). Used when valueType === 'monetary'. */
  monetaryEstimate: string;
  tools: string[];
  cost: string;
  savings: string;
  improvements: string;
  /**
   * Business-case line-item presence, driven by the `useBusinessCase` hook
   * (US5/US7). For BUSINESS_CASE tenants the `bizcase` tab is complete when both
   * are true; the retired scalar `cost`/`savings` fields no longer drive it.
   */
  hasCost: boolean;
  hasSavings: boolean;
}

/**
 * The five named strategic-value levels, mirroring Vane Loop's Business Case
 * tab (`frontend/src/apps/products-detail/views/business-case-tab.ts`). The two
 * frontends share no module graph, so this constant is intentionally duplicated
 * into the portal rather than imported.
 */
export const STRATEGIC_VALUE_OPTIONS: ReadonlyArray<{ label: string; value: number }> = [
  { label: 'Very Low', value: 1 },
  { label: 'Low', value: 3 },
  { label: 'Medium', value: 5 },
  { label: 'High', value: 7 },
  { label: 'Very High', value: 9 },
];

export const ALL_TABS: TabId[] = ['details', 'tools', 'value', 'bizcase', 'improvements'];

export const TAB_LABELS: Record<TabId, string> = {
  details: 'Details',
  tools: 'Tools used',
  value: 'Value',
  bizcase: 'Business case',
  improvements: 'Improvements',
};

export function emptyForm(): EditorForm {
  return {
    name: '',
    description: '',
    domainId: null,
    valueType: null,
    importance: 0,
    justification: '',
    monetaryEstimate: '',
    tools: [],
    cost: '',
    savings: '',
    improvements: '',
    hasCost: false,
    hasSavings: false,
  };
}

/**
 * Which tabs are visible for a tenant tier. Standard base always shows Details,
 * Tools, Value. Business case + Improvements are gated by the add-on flags
 * (AC4.4). Hidden tabs never render and never count toward completion.
 */
export function visibleTabs(tier: TierResponse): TabId[] {
  // BUSINESS_CASE tenants express value through the cost/savings business case,
  // so the Value tab is hidden entirely for them (US7). Standard-only tenants
  // keep Details / Tools / Value exactly as before.
  const tabs: TabId[] = tier.business_case
    ? ['details', 'tools']
    : ['details', 'tools', 'value'];
  if (tier.business_case) tabs.push('bizcase');
  if (tier.improvements) tabs.push('improvements');
  return tabs;
}

function numericFilled(v: string): boolean {
  if (v.trim() === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
}

/** Per-tab completion fraction in [0, 1]. */
export function tabFraction(tab: TabId, form: EditorForm): number {
  switch (tab) {
    case 'details':
      return (form.name.trim() ? 0.5 : 0) + (form.description.trim() ? 0.5 : 0);
    case 'tools':
      return form.tools.length > 0 ? 1 : 0;
    case 'value': {
      const typeScore = form.valueType ? 0.5 : 0;
      const detailScore =
        form.valueType === 'monetary'
          ? (numericFilled(form.monetaryEstimate) ? 0.5 : 0)
          : (form.importance > 0 ? 0.5 : 0);
      return typeScore + detailScore;
    }
    case 'bizcase':
      // The Business case tab is only visible for BUSINESS_CASE tenants, whose
      // value is expressed as real cost/savings line items. Completion derives
      // from line-item presence (hasCost / hasSavings), not the retired scalar
      // cost/savings inputs.
      return (form.hasCost ? 0.5 : 0) + (form.hasSavings ? 0.5 : 0);
    case 'improvements':
      return form.improvements.trim() ? 1 : 0;
  }
}

export function stateOf(fraction: number): TabState {
  if (fraction >= 1) return 'done';
  if (fraction > 0) return 'partial';
  return 'todo';
}

/** Tabs (among the visible set) that are not yet fully complete. */
export function incompleteTabs(form: EditorForm, tier: TierResponse): TabId[] {
  return visibleTabs(tier).filter((t) => tabFraction(t, form) < 1);
}

export function allComplete(form: EditorForm, tier: TierResponse): boolean {
  return incompleteTabs(form, tier).length === 0;
}

/** Human-readable "N sections still need attention" sentence. */
export function attentionSentence(count: number): string {
  const plural = count === 1 ? '' : 's';
  const verb = count === 1 ? 's' : '';
  return `${count} section${plural} still need${verb} attention.`;
}
